import { useState } from "react";
import { toast } from "sonner";
import { PawPrint } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending: "Pending Approval",
  approved: "Approved — Ready to Order",
  rejected: "Pre-Order Unavailable",
  ordered: "Ordered",
};

export const PreorderPanel = ({ product, qty, onApprovedAdd }) => {
  const [form, setForm] = useState({
    name: "",
    email: localStorage.getItem("lamsah_preorder_email") || "",
    phone: "",
  });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill your name, email and phone.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/preorders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: product.id, qty, ...form }),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      localStorage.setItem("lamsah_preorder_email", form.email.trim());
      setStatus("pending");
      toast.success(data.message);
    } catch (err) {
      toast.error(err?.detail || "Could not submit the request.");
    } finally {
      setBusy(false);
    }
  };

  const check = async () => {
    if (!form.email.trim()) {
      toast.error("Enter your email to check your request.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/preorders/check?email=${encodeURIComponent(form.email.trim())}&product_id=${product.id}`);
      const data = await r.json();
      const latest = (data.preorders || [])[0];
      if (!latest) {
        toast("No pre-order request found for this email and shade.");
        setStatus(null);
      } else {
        setStatus(latest.status);
      }
    } catch {
      toast.error("Could not check the status.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-9 border border-espresso/20 bg-white p-6" data-testid="preorder-panel">
      <span className="inline-flex items-center gap-2 bg-espresso text-ivory text-[10px] uppercase tracking-[0.25em] px-3 py-1.5" data-testid="preorder-badge">
        <PawPrint className="w-3.5 h-3.5" strokeWidth={1.5} /> Available on Pre-Order
      </span>
      <p className="text-sm text-espresso/70 leading-relaxed mt-4">
        SAFARI pieces are reserved personally. Request your shade and quantity — once approved,
        you'll be able to place your order. No payment is taken until approval.
      </p>

      {status === "approved" ? (
        <div className="mt-5">
          <p className="text-sm font-medium text-green-700" data-testid="preorder-status">Approved — Ready to Order</p>
          <button data-testid="preorder-add-to-bag" onClick={onApprovedAdd} className="btn-primary w-full mt-4">
            Add to Bag
          </button>
        </div>
      ) : (
        <>
          {status && (
            <p className="text-sm mt-4 font-medium" data-testid="preorder-status">
              Status: {STATUS_LABEL[status] || status}
            </p>
          )}
          <div className="grid grid-cols-1 gap-3 mt-5">
            <input data-testid="preorder-name" className="input-lux" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="preorder-email" type="email" className="input-lux" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="preorder-phone" className="input-lux" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="preorder-submit" disabled={busy} onClick={submit} className="btn-primary flex-1">
              {busy ? "Please wait…" : "Request Pre-Order"}
            </button>
            <button data-testid="preorder-check" disabled={busy} onClick={check} className="btn-secondary !px-5">
              Check Status
            </button>
          </div>
        </>
      )}
    </div>
  );
};
